<template>
  <div class="section overflow-hidden position-relative" id="wrapper">

    <!-- Page Content Wrapper Start -->
    <div class="page-content-wrapper padding-left-0">
      <div class="container-fluid custom-container">

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <p>Loading users...</p>
        </div>

        <!-- Error State -->
        <div v-if="error" class="error-state">
          <p class="text-danger">{{ error }}</p>
          <button class="btn btn-primary" @click="loadUsers">Retry</button>
        </div>

        <!-- Users Lists -->
        <div v-if="!loading && !error" class="section section-padding mt-n10 padding-top-50">
          
          <!-- Managers & Members List -->
          <section class="user-list">
            <div class="section-header">
              <h2>Managers & Members</h2>
              <span class="user-count">{{ managersAndMembers.length }} users</span>
            </div>
            <p>Organization managers and team members with package details.</p>
            
            <Table 
              v-if="managersAndMembers.length > 0"
              :data="managersAndMembers"
              :columnsToShow="['name', 'email', 'phone_number', 'organisation', 'role', 'subscriptions', 'active']"
            >
              <template #cell-name="{ row }">
                <div class="user-info" @click='gotoresult(row._id)' title="Click to check user result">
                  <div class="profile-icon">{{ row.first_name.charAt(0) }}{{ row.last_name.charAt(0) }}</div>
                  <p class="user-name">{{ row.first_name }} {{ row.last_name }}</p>
                </div>
              </template>
              <template #cell-organisation="{ row }">
                <p class="cap">{{ row.organisation || '-' }}</p>
              </template>
              <template #cell-role="{ row }">
                <p class="badge cap" :class="getBadgeClass(row.role)">
                  {{ row.role }}
                </p>
              </template>
              <template #cell-subscriptions="{ row }">
                <div v-if='row.subscriptions && row.subscriptions.length > 0'>
                  <p class="badge cap" v-for='sub in row.subscriptions' :class="getBadgeClass(sub.package_name)" :key='sub.subscription_start'>
                    {{ sub.package_name }}
                  </p>
                </div>
                <div v-else>
                  <p class="text-muted">No subscription</p>
                </div>
              </template>
              <template #cell-active="{ row }">
                <div v-if='row.subscriptions && row.subscriptions.length > 0'>
                  <p class="cap" v-for='sub in row.subscriptions' :key='sub.subscription_start'>
                    {{ sub.active ? 'Active' : 'Inactive' }}
                  </p>
                </div>
                <div v-else>
                  <p class="text-muted">-</p>
                </div>
              </template>
            </Table>

            <!-- Empty State for Managers & Members -->
            <div v-else class="empty-state">
              <p>No managers or members found.</p>
            </div>
          </section>

          <!-- Separator -->
          <div class="list-separator"></div>

          <!-- Admins List -->
          <section class="user-list admin-section">
            <div class="section-header">
              <h2>Administrators</h2>
              <span class="user-count">{{ admins.length }} users</span>
            </div>
            <p>System administrators with full access.</p>
            
            <Table 
              v-if="admins.length > 0"
              :data="admins"
              :columnsToShow="['name', 'email', 'phone_number', 'role', 'created_date']"
              :headerLabels="{created_date: 'Created On'}"
            >
              <template #cell-name="{ row }">
                <div class="user-info">
                  <div class="profile-icon admin-icon">{{ row.first_name.charAt(0) }}{{ row.last_name.charAt(0) }}</div>
                  <p class="user-name">{{ row.first_name }} {{ row.last_name }}</p>
                </div>
              </template>
              <template #cell-role="{ row }">
                <p class="badge cap" :class="getBadgeClass(row.role)">
                  {{ row.role }}
                </p>
              </template>
              <template #cell-created_date="{ row }">
                <p>{{ formatDate(row.created_date) }}</p>
              </template>
            </Table>

            <!-- Empty State for Admins -->
            <div v-else class="empty-state">
              <p>No administrators found.</p>
            </div>
          </section>

        </div>
        <!-- Lists End -->

      </div>
    </div>
    <!-- Page Content Wrapper End -->
  </div>
</template>


<script>

import { fetchAllUsers } from '@/services/adminService';
import Table from '@/components/reusable/Table.vue';

export default {
  name: 'RegisteredUserDetails',
  components: {Table},
  data() {
    return {
      users: [],
      loading: false,
      error: null
    };
  },

  computed: {
    // Filter users: managers and members only
    managersAndMembers() {
      return this.users.filter(user => 
        user.role === 'manager' || user.role === 'member'
      );
    },

    // Filter users: admins only
    admins() {
      return this.users.filter(user => user.role === 'admin');
    }
  },

  methods:{
    gotoresult(id){
      const url = '/dashboard/admin/view-results/' + id;
      this.$router.push(url);
    },
    
    getBadgeClass(txt) {
      console.log(txt);
      switch (txt) {
        case 'admin':
          return 'badge-admin';
        case 'manager':
          return 'badge-manager';
        case 'member':
          return 'badge-member';
        // Package names
        case 'Beginner':
        case 'Basic':
          return 'badge-dark';
        case 'Standard':
        case 'Intermediate':
          return 'badge-primary';
        case 'Premium':
        case 'Advanced':
          return 'badge-danger';
        default:
          return 'badge-secondary'; // Default class for unknown
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric'
      });
    },

    async loadUsers() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await fetchAllUsers();
        console.log('Raw response from fetchAllUsers:', response);
        console.log('Type of response:', typeof response);
        console.log('Is response an array?', Array.isArray(response));
        
        // Since fetchAllUsers already returns response.data, 
        // we need to check what that structure is
        if (Array.isArray(response)) {
          // If response is directly an array of users
          this.users = response;
        } 
        else if (response && Array.isArray(response.users)) {
          // If response is { users: [...] }
          this.users = response.users;
        }
        else if (response && typeof response === 'object') {
          // If response is an object, try to find the array
          // Check common property names
          const possibleArrayKeys = ['users', 'data', 'items', 'results'];
          let found = false;
          
          for (const key of possibleArrayKeys) {
            if (Array.isArray(response[key])) {
              this.users = response[key];
              found = true;
              console.log(`Found users array in response.${key}`);
              break;
            }
          }
          
          if (!found) {
            console.error('Could not find users array in response. Response keys:', Object.keys(response));
            console.error('Full response structure:', JSON.stringify(response, null, 2));
            this.error = 'Received data in unexpected format. Check console for details.';
            this.users = [];
          }
        }
        else {
          console.error('Unexpected response format:', response);
          this.error = 'Received data in unexpected format. Check console for details.';
          this.users = [];
        }
        
        console.log('Users array after processing:', this.users);
        console.log('Total users:', this.users.length);
        console.log('Managers & Members:', this.managersAndMembers.length);
        console.log('Admins:', this.admins.length);
        
        if (this.users.length === 0) {
          console.warn('No users found in the response');
        }
        
      } catch (error) {
        console.error('Failed to load users:', error);
        console.error('Error details:', error.response || error.message);
        this.error = `Failed to load users: ${error.message || 'Unknown error'}`;
        this.users = [];
      } finally {
        this.loading = false;
      }
    }
  },

  async created() {
    await this.loadUsers();
  },
}
</script>

<style scoped>
.user-info {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.profile-icon {
  flex: 0 0 auto; /* Prevents the icon from growing or shrinking */
  width: 36px; /* Fixed width */
  height: 36px; /* Fixed height */
  border-radius: 50%;
  background-color: #007bff;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 10px;
  font-size: 14px;
}

.admin-icon {
  background-color: #dc3545;
}

.user-name {
  margin: 0;
  font-weight: 500;
  color: #333;
}

.cap {
  text-transform: capitalize;
  margin: 0;
}

.badge {
  display: inline-block;
  padding: 0.35em 0.65em;
  font-size: 0.85em;
  font-weight: 600;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  vertical-align: baseline;
  border-radius: 0.375rem;
  margin: 2px;
}

.badge-admin {
  background-color: #dc3545;
  color: white !important;
}

.badge-manager {
  background-color: #ffc107;
  color: #000 !important;
}

.badge-member {
  background-color: #28a745;
  color: white !important;
}

.badge-dark {
  background-color: #343a40;
  color: white !important;
}

.badge-primary {
  background-color: #007bff;
  color: white !important;
}

.badge-danger {
  background-color: #dc3545;
  color: white !important;
}

.badge-secondary {
  background-color: #6c757d;
  color: white !important;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 40px;
}

.error-state .btn {
  margin-top: 16px;
}

/* Section Styling */
.user-list {
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 30px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.section-header h2 {
  margin: 0;
  font-size: 24px;
  color: #333;
}

.user-count {
  background: #f0f0f0;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  color: #666;
}

.user-list p {
  color: #666;
  margin-bottom: 20px;
}

/* Admin Section Specific Styling */
.admin-section {
  border-top: 4px solid #dc3545;
}

.admin-section .section-header h2 {
  color: #dc3545;
}

.admin-section .user-count {
  background: #f8d7da;
  color: #721c24;
}

/* List Separator */
.list-separator {
  height: 40px;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #999;
  background: #f8f9fa;
  border-radius: 8px;
}

.empty-state p {
  margin: 0;
  font-size: 16px;
}

/* Text Utilities */
.text-muted {
  color: #6c757d;
  font-style: italic;
  font-size: 14px;
}

.text-danger {
  color: #dc3545;
}
</style>